import {acceptHMRUpdate, defineStore} from "pinia";
import {CpsLocatorReport, CpsPost} from "@/core/interfaces/CpsLocator";

/**
 * Whether a card of that family can sign in to amelipro.
 *
 * @param family The family the card reported, when it could be read.
 */
export const canSignIn = (family?: string): boolean => !family || family === 'CPS';

interface CpsStore {
    /** The last known CPS state of every workstation of the pharmacy, by host name. */
    posts: Record<string, CpsPost>;
    /** The host name of this workstation, once the shell has given it. */
    hostname: string | null;
    /** Whether a location round is still collecting answers. */
    scanning: boolean;
    /** When the last location round closed. */
    scannedAt: string | null;
}

/**
 * The pharmacy's CPS map, as this Connect knows it.
 *
 * Each workstation reports its own reader on the pharmacy channel, and every
 * Connect stores every report that passes — so any screen can tell where the
 * physical card is, and send the pharmacist to the right post, without asking
 * the others and waiting for their answers.
 */
export const useCpsStore = defineStore('cps', {
    state: (): CpsStore => ({
        posts: {},
        hostname: null,
        scanning: false,
        scannedAt: null
    }),
    getters: {
        /** The workstations holding a CPS card, the most recently seen first. */
        cardPosts(): CpsPost[] {
            return Object.values(this.posts)
                .filter(post => post.present)
                .sort((first, second) => second.reportedAt.localeCompare(first.reportedAt));
        },
        /** The other workstations holding a CPS card: where to send the pharmacist. */
        otherCardPosts(): CpsPost[] {
            return this.cardPosts.filter(post => post.hostname !== this.hostname);
        },
        /**
         * The workstations whose card can open an amelipro session.
         *
         * Only a CPS proper signs in to the portal: the service cards — CPA,
         * CPE, CPF, CDE — are turned away at the door. A card whose family
         * could not be read is given the benefit of the doubt.
         */
        signInPosts(): CpsPost[] {
            return this.cardPosts.filter(post => canSignIn(post.family));
        },
        /** The workstations that answer the pharmacy's ADAC calls, the most recently seen first. */
        gatewayPosts(): CpsPost[] {
            return this.cardPosts.filter(post => post.gateway === true);
        },
        /** A gateway on another workstation, to send this post's ADAC calls to. */
        remoteGateway(): CpsPost | null {
            return this.gatewayPosts.find(post => post.hostname !== this.hostname) ?? null;
        },
        /** Whether this very workstation is a gateway. */
        isLocalGateway(): boolean {
            return this.localPost?.gateway === true;
        },
        /** Whether the pharmacy has a gateway at all, here or elsewhere. */
        hasGateway(): boolean {
            return this.gatewayPosts.length > 0;
        },
        /** What is known of this very workstation. */
        localPost(): CpsPost | null {
            return this.hostname ? this.posts[this.hostname] ?? null : null;
        },
        /** Whether a CPS card answered on this workstation. */
        hasLocalCard(): boolean {
            return this.localPost?.present === true;
        },
        /** Whether a location round has already closed, so an empty map means something. */
        located(): boolean {
            return this.scannedAt !== null;
        }
    },
    actions: {
        /**
         * Remember which workstation this Connect runs on.
         *
         * @param hostname The host name given by the shell.
         */
        identify(hostname: string | null) {
            this.hostname = hostname;
        },
        /**
         * Store what a workstation reports about its reader.
         *
         * The freshness is stamped here rather than read from the report: the
         * workstations of a pharmacy have no reason to share a clock.
         *
         * @param report The report whispered by a workstation, or built locally.
         */
        report(report: CpsLocatorReport) {
            if (!report?.hostname) {
                return;
            }

            this.posts[report.hostname] = {
                hostname: report.hostname,
                present: report.present,
                holder: report.holder,
                civility: report.civility,
                family: report.family,
                title: report.title,
                entity: report.entity,
                validUntil: report.validUntil,
                gateway: report.gateway === true,
                reportedAt: new Date().toISOString()
            };
        },
        /**
         * Open a location round: the answers are on their way.
         */
        beginScan() {
            this.scanning = true;
        },
        /**
         * Close the location round, whatever it collected.
         */
        endScan() {
            this.scanning = false;
            this.scannedAt = new Date().toISOString();
        }
    }
});

//https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCpsStore, import.meta.hot));
}
